import { useConfirmation } from "src/sudokit/components/ConfirmationDialog/ConfirmationProvider";
import React, { useEffect, useMemo, useState } from "react";

import { SudoButton } from "src/sudokit/components/SudoButton";
import Auth from "../Auth";

type ProviderSummary = {
  providerId?: string;
  uid?: string;
};

type SubscriptionSummary = {
  id?: string;
  email?: string;
  stripeCustomerId?: string;
  activeSubscription?: boolean | null;
};

type DeletionResult = {
  facebookUserId: string;
  status: string;
  message: string | null;
  activeSubscription: boolean | null;
  scheduledDeletionAt?: string | null;
  facebookDeletionRequestedAt?: string | null;
  user?: {
    uid: string;
    email?: string | null;
    providers?: ProviderSummary[];
  } | null;
  subscription?: SubscriptionSummary | null;
};

type PendingDeletion = {
  uid: string;
  email: string | null;
  scheduledDeletionAt: string | null;
  requestedAt: string | null;
  activeSubscription: boolean | null;
};

type ApiResponse = {
  success: boolean;
  dryRun: boolean;
  result?: DeletionResult;
  results?: DeletionResult[];
  message?: string;
};

type CancelResponse = {
  success: boolean;
  message?: string;
  status?: string | null;
  scheduledDeletionAt?: string | null;
  resumedStripe?: boolean;
};

export default function FacebookDeletionTester(): JSX.Element {
  const confirmAction = useConfirmation();
  const [facebookUserId, setFacebookUserId] = useState("");
  const [dryRun, setDryRun] = useState(true);
  const [skipActiveSubscriptionCheck, setSkipActiveSubscriptionCheck] = useState(false);
  const [detachStripeLinks, setDetachStripeLinks] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [singleResult, setSingleResult] = useState<DeletionResult | null>(null);
  const [singleResponseMeta, setSingleResponseMeta] = useState<{ success: boolean; dryRun: boolean } | null>(null);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [csvLoading, setCsvLoading] = useState(false);
  const [csvError, setCsvError] = useState<string | null>(null);
  const [csvResults, setCsvResults] = useState<DeletionResult[]>([]);
  const [csvDryRun, setCsvDryRun] = useState(true);
  const [csvSkipActive, setCsvSkipActive] = useState(false);
  const [cancelState, setCancelState] = useState<{ loading: boolean; error: string | null; successMessage: string | null }>({
    loading: false,
    error: null,
    successMessage: null,
  });
  const [pending, setPending] = useState<PendingDeletion[]>([]);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [pendingError, setPendingError] = useState<string | null>(null);

  const loadPending = async () => {
    setPendingLoading(true);
    setPendingError(null);
    try {
      const response = await Auth.fetchWithIdToken("/api/admin/facebook-deletion/pending", {});
      if (!response.success) {
        throw new Error(response.message || "Failed to load pending deletions");
      }
      const normalized: PendingDeletion[] = (response.pending || []).map((item: any) => ({
        uid: item.uid,
        email: item.email ?? null,
        scheduledDeletionAt: item.facebookDeletionScheduledAt ?? null,
        requestedAt: item.facebookDeletionRequestedAt ?? null,
        activeSubscription: typeof item.activeSubscription === "boolean" ? item.activeSubscription : null,
      }));
      setPending(normalized);
    } catch (err: any) {
      console.error(err);
      setPendingError(err?.message || "Failed to load pending deletions");
    } finally {
      setPendingLoading(false);
    }
  };

  useEffect(() => {
    loadPending();
  }, []);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmedId = facebookUserId.trim();
    if (!trimmedId) {
      setError("Facebook user ID is required");
      return;
    }
    const runConfirmedAction = async () => {
      setLoading(true);
      setError(null);
      setSingleResult(null);
      setSingleResponseMeta(null);
      setCancelState({
        loading: false,
        error: null,
        successMessage: null
      });
      try {
        const response: ApiResponse = await Auth.fetchWithIdToken("/api/admin/facebookDeletionTest", {
          facebookUserId: trimmedId,
          dryRun,
          skipActiveSubscriptionCheck,
          detachStripeLinks: dryRun ? false : detachStripeLinks
        });
        setSingleResult(response.result ?? null);
        setSingleResponseMeta({
          success: response.success,
          dryRun: response.dryRun
        });
      } catch (err: any) {
        console.error(err);
        let message = err?.message || "Failed to process request";
        let payload: ApiResponse | null = null;
        if (err?.response) {
          try {
            payload = await err.response.clone().json();
          } catch (jsonErr) {
            try {
              const text = await err.response.clone().text();
              if (text) {
                message = `${message}: ${text}`;
              }
            } catch (textErr) {
              console.error(textErr);
            }
          }
          if (payload?.message && typeof payload.message === "string") {
            message = payload.message;
          }
        }
        if (payload) {
          setSingleResult(payload.result ?? null);
          setSingleResponseMeta({
            success: payload.success,
            dryRun: payload.dryRun
          });
        }
        setError(message);
      } finally {
        setLoading(false);
      }
    };
    if (!dryRun) {
      await confirmAction({
        title: "Permanently delete account",
        message: "This will permanently delete the linked Sudowrite account and cannot be undone. Continue?",
        actionLabel: "Delete account",
        destructive: true
      }, runConfirmedAction);
    } else {
      await runConfirmedAction();
    }
  };

  const handleCsvFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCsvError(null);
    setCsvResults([]);
    const file = event.target.files?.[0] ?? null;
    setCsvFile(file);
  };

  const parseCsv = async (file: File): Promise<string[]> => {
    const text = await file.text();
    return text
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => {
        const commaIndex = line.indexOf(",");
        const tabIndex = line.indexOf("\t");
        let endIndex = -1;
        if (commaIndex >= 0 && tabIndex >= 0) {
          endIndex = Math.min(commaIndex, tabIndex);
        } else if (commaIndex >= 0) {
          endIndex = commaIndex;
        } else if (tabIndex >= 0) {
          endIndex = tabIndex;
        }

        const cell = endIndex >= 0 ? line.slice(0, endIndex) : line;
        return cell.replace(/^"|"$/g, "").trim();
      })
      .filter((id): id is string => Boolean(id));
  };

  const handleCsvSubmit = async () => {
    if (!csvFile) {
      setCsvError("Please choose a CSV file with Facebook user IDs");
      return;
    }

    setCsvError(null);
    setCsvLoading(true);
    setCsvResults([]);

    try {
      const ids = await parseCsv(csvFile);
      if (ids.length === 0) {
        setCsvError("No IDs found in the CSV file");
        setCsvLoading(false);
        return;
      }

      const response: ApiResponse = await Auth.fetchWithIdToken("/api/admin/facebookDeletionTest", {
        facebookUserIds: ids,
        dryRun: csvDryRun,
        skipActiveSubscriptionCheck: csvSkipActive,
        detachStripeLinks: false,
      });

      setCsvResults(response.results ?? []);
    } catch (err: any) {
      console.error(err);
      let message = err?.message || "Failed to process CSV";
      if (err?.response) {
        try {
          const payload: ApiResponse = await err.response.clone().json();
          if (payload?.message) {
            message = payload.message;
          }
          if (payload?.results) {
            setCsvResults(payload.results);
          }
        } catch (jsonErr) {
          console.error(jsonErr);
        }
      }
      setCsvError(message);
    } finally {
      setCsvLoading(false);
    }
  };

  const aggregateCsvSummary = useMemo(() => {
    if (csvResults.length === 0) {
      return null;
    }

    const counts: Record<string, number> = {};
    let activeSubs = 0;
    csvResults.forEach((item) => {
      counts[item.status] = (counts[item.status] || 0) + 1;
      if (item.activeSubscription) {
        activeSubs += 1;
      }
    });

    return { counts, activeSubs };
  }, [csvResults]);

  const formatDateTime = (value?: string | null): string | null => {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString();
  };

  const handleCancelScheduledDeletion = async () => {
    if (!singleResult?.facebookUserId) return;
    await confirmAction({
      title: "Cancel scheduled deletion",
      message: "Cancel the scheduled deletion and resume the user's subscription?",
      actionLabel: "Resume subscription",
      destructive: false
    }, async () => {
      setCancelState({
        loading: true,
        error: null,
        successMessage: null
      });
      try {
        const response: CancelResponse = await Auth.fetchWithIdToken("/api/admin/facebookDeletionCancel", {
          facebookUserId: singleResult.facebookUserId
        });
        if (!response.success) {
          throw new Error(response.message || "Failed to cancel scheduled deletion");
        }
        setCancelState({
          loading: false,
          error: null,
          successMessage: "Scheduled deletion cancelled"
        });
        setSingleResult(prev => prev ? {
          ...prev,
          status: "canceled",
          message: "Scheduled deletion canceled",
          scheduledDeletionAt: null
        } : prev);
        loadPending();
      } catch (err: any) {
        console.error(err);
        setCancelState({
          loading: false,
          error: err?.message || "Failed to cancel scheduled deletion",
          successMessage: null
        });
      }
    });
  };

  return (
    <div className="p-6 overflow-auto h-full text-app-grey-0">
      <h1 className="text-2xl font-semibold mb-4">Facebook Data Deletion</h1>
      <div>
        <div className="flex items-start justify-between">
          <h2 className="text-xl font-semibold mb-4">Scheduled deletions</h2>
          <SudoButton
            variant="secondary"
            onClick={loadPending}
            disabled={pendingLoading}
          >
            {pendingLoading ? "Refreshing…" : "Refresh"}
          </SudoButton>
        </div>

        {pendingError && <div className="mb-3 text-sm text-red-400">{pendingError}</div>}

        {pending.length === 0 && !pendingLoading && <div className="text-sm text-app-grey-1">No scheduled deletions found.</div>}

        {pending.length > 0 && (
          <div className="overflow-auto">
            <table className="min-w-full text-sm border border-app-divider">
              <thead className="bg-app-menu-color-background">
                <tr>
                  <th className="px-3 py-2 border-b border-app-divider text-left">UID</th>
                  <th className="px-3 py-2 border-b border-app-divider text-left">Email</th>
                  <th className="px-3 py-2 border-b border-app-divider text-left">Scheduled For</th>
                  <th className="px-3 py-2 border-b border-app-divider text-left">Requested At</th>
                  <th className="px-3 py-2 border-b border-app-divider text-left">Active?</th>
                </tr>
              </thead>
              <tbody>
                {pending.map((item) => (
                  <tr key={item.uid} className="odd:bg-app-card-background even:bg-app-document-background">
                    <td className="px-3 py-2 border-b border-app-divider font-mono text-xs">{item.uid}</td>
                    <td className="px-3 py-2 border-b border-app-divider">{item.email || ""}</td>
                    <td className="px-3 py-2 border-b border-app-divider">{formatDateTime(item.scheduledDeletionAt) || ""}</td>
                    <td className="px-3 py-2 border-b border-app-divider">{formatDateTime(item.requestedAt) || ""}</td>
                    <td className="px-3 py-2 border-b border-app-divider">{item.activeSubscription ? "Yes" : "No"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <hr className="my-8 border-app-divider" />
      <h2 className="text-xl font-semibold mb-4">Single Account Deletion</h2>
      <p className="mb-6 text-app-grey-1 max-w-2xl">
        This tool runs the same deletion flow used for Facebook data deletion callbacks. Provide the Facebook app-scoped user ID, choose whether to run a dry run, and submit to see the outcome. Dry runs perform every lookup without deleting the account.
      </p>

      <form
        onSubmit={handleSubmit}
        className="space-y-6 max-w-2xl"
      >
        <div>
          <label
            htmlFor="facebook-user-id"
            className="block text-sm font-medium mb-1"
          >
            Facebook user ID
          </label>
          <input
            id="facebook-user-id"
            type="text"
            value={facebookUserId}
            onChange={(event) => setFacebookUserId(event.target.value)}
            className="w-full px-3 py-2 border border-app-divider rounded bg-app-document-background focus:outline-none focus:ring-2 focus:ring-app-selected-element-background"
            placeholder="e.g. 123456789012345"
            disabled={loading}
          />
        </div>

        <div className="space-y-2">
          <label className="flex items-center space-x-2 text-sm">
            <input
              type="checkbox"
              checked={dryRun}
              onChange={(event) => setDryRun(event.target.checked)}
              disabled={loading}
            />
            <span>Dry run (recommended)</span>
          </label>

          <label className="flex items-center space-x-2 text-sm">
            <input
              type="checkbox"
              checked={skipActiveSubscriptionCheck}
              onChange={(event) => setSkipActiveSubscriptionCheck(event.target.checked)}
              disabled={loading}
            />
            <span>Skip active subscription check (matches Facebook webhook behaviour)</span>
          </label>

          <label className="flex items-center space-x-2 text-sm">
            <input
              type="checkbox"
              checked={detachStripeLinks}
              onChange={(event) => setDetachStripeLinks(event.target.checked)}
              disabled={loading || dryRun}
            />
            <span>Detach Stripe linkage before deletion</span>
          </label>
          <p className="text-xs text-app-grey-2">
            Stripe detachment is only applied when not running a dry run.
          </p>
        </div>

        <SudoButton
          type="submit"
          variant='primary'
          disabled={loading || !facebookUserId.trim()}
        >
          {loading ? "Processing…" : dryRun ? "Run dry test" : "Delete account"}
        </SudoButton>
      </form>

      {error && (
        <div className="mt-6 p-3 border border-red-500 bg-red-900/20 text-red-800 rounded">
          {error}
        </div>
      )}

      {singleResult && (
        <div className="mt-6">
          <h2 className="text-lg font-semibold mb-2">Single Result</h2>
          <div className="text-sm space-y-2">
            <div>
              <span className="font-medium">Status:</span> {singleResult.status}
            </div>
            {singleResult.message && (
              <div>
                <span className="font-medium">Message:</span> {singleResult.message}
              </div>
            )}
            <div>
              <span className="font-medium">Active subscription:</span> {singleResult.activeSubscription ? "Yes" : "No"}
            </div>
            {singleResult.scheduledDeletionAt && (
              <div>
                <span className="font-medium">Scheduled deletion:</span> {formatDateTime(singleResult.scheduledDeletionAt)}
              </div>
            )}
          </div>

          {singleResult.status === "scheduled" && (
            <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:space-x-3 space-y-2 sm:space-y-0">
              <button
                type="button"
                onClick={handleCancelScheduledDeletion}
                className={`px-4 py-2 rounded text-sm font-medium ${cancelState.loading ? "bg-app-divider text-app-grey-2" : "bg-app-selected-element-background text-app-selected-element-text"}`}
                disabled={cancelState.loading}
              >
                {cancelState.loading ? "Cancelling…" : "Cancel scheduled deletion"}
              </button>
              {cancelState.error && <span className="text-sm text-red-400">{cancelState.error}</span>}
              {cancelState.successMessage && <span className="text-sm text-green-400">{cancelState.successMessage}</span>}
            </div>
          )}

          <details className="mt-3">
            <summary className="cursor-pointer text-sm text-app-selected-element-text">View raw response</summary>
            <pre className="mt-2 bg-app-document-background border border-app-divider rounded p-4 overflow-auto text-xs whitespace-pre-wrap">
              {JSON.stringify({ meta: singleResponseMeta, result: singleResult }, null, 2)}
            </pre>
          </details>
        </div>
      )}

      <hr className="my-8 border-app-divider" />

      <div>
        <h2 className="text-xl font-semibold mb-4">CSV Tester</h2>
        <p className="text-sm text-app-grey-1 mb-4 max-w-2xl">Upload a CSV (single column of Facebook user IDs). The IDs are tested in dry-run mode so no deletions or notifications occur.</p>

        <div className="space-y-3 max-w-2xl">
          <input
            type="file"
            accept=".csv,text/csv"
            onChange={handleCsvFileChange}
            disabled={csvLoading}
            className="text-sm"
          />

          <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 space-y-2 sm:space-y-0 text-sm">
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={csvDryRun}
                onChange={(event) => setCsvDryRun(event.target.checked)}
                disabled={csvLoading}
              />
              <span>Dry run</span>
            </label>
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={csvSkipActive}
                onChange={(event) => setCsvSkipActive(event.target.checked)}
                disabled={csvLoading}
              />
              <span>Skip active subscription check</span>
            </label>
          </div>

          <SudoButton
            variant='primary'
            onClick={handleCsvSubmit}
            disabled={csvLoading || !csvFile}
          >
            {csvLoading ? "Processing…" : "Run CSV Test"}
          </SudoButton>
        </div>

        {csvError && (
          <div className="mt-4 p-3 border border-red-500 bg-red-900/20 text-red-200 rounded text-sm">{csvError}</div>
        )}

        {aggregateCsvSummary && (
          <div className="mt-6 text-sm">
            <h3 className="font-semibold mb-2">Summary</h3>
            <p>Total IDs: {csvResults.length}</p>
            <p>Active subscriptions detected: {aggregateCsvSummary.activeSubs}</p>
            <ul className="mt-2 space-y-1">
              {Object.entries(aggregateCsvSummary.counts).map(([status, count]) => (
                <li key={status}>
                  <span className="font-medium">{status}</span>: {count}
                </li>
              ))}
            </ul>
          </div>
        )}

        {csvResults.length > 0 && (
          <div className="mt-6 overflow-auto">
            <table className="min-w-full text-sm border border-app-divider">
              <thead className="bg-app-menu-color-background">
                <tr>
                  <th className="px-3 py-2 border-b border-app-divider text-left">Facebook ID</th>
                  <th className="px-3 py-2 border-b border-app-divider text-left">Status</th>
                  <th className="px-3 py-2 border-b border-app-divider text-left">Active Sub</th>
                  <th className="px-3 py-2 border-b border-app-divider text-left">Scheduled Deletion</th>
                  <th className="px-3 py-2 border-b border-app-divider text-left">Message</th>
                </tr>
              </thead>
              <tbody>
                {csvResults.map((item) => (
                  <tr key={item.facebookUserId} className="odd:bg-app-card-background even:bg-app-document-background">
                    <td className="px-3 py-2 border-b border-app-divider font-mono text-xs">{item.facebookUserId}</td>
                    <td className="px-3 py-2 border-b border-app-divider">{item.status}</td>
                    <td className="px-3 py-2 border-b border-app-divider">{item.activeSubscription ? "Yes" : "No"}</td>
                    <td className="px-3 py-2 border-b border-app-divider">{formatDateTime(item.scheduledDeletionAt) ?? ""}</td>
                    <td className="px-3 py-2 border-b border-app-divider">{item.message || ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>


    </div>
  );
}
